import React, { useState, useEffect } from "react";
import { GaugeIcon } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "../config/api";

export default function LiveResults() {
  // Holds the human-readable association name purely for display in the heading
  const [associationNameForDisplay, setAssociationNameForDisplay] = useState("");

  // Iframe + loading + error
  const [iframeUrl, setIframeUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Settings/loading state for initial UI message
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  // Family associations and selected value.
  // IMPORTANT: selectedAssociation will be either 'All' or an associationKey string.
  const [familyAssociations, setFamilyAssociations] = useState([]);
  const [selectedAssociation, setSelectedAssociation] = useState("");

  useEffect(() => {
    loadFamilyAssociations();
  }, []);

  // Default selection once associations load
  useEffect(() => {
    if (familyAssociations.length > 0 && !selectedAssociation) {
      setSelectedAssociation("All");
    }
  }, [familyAssociations, selectedAssociation]);

  const loadFamilyAssociations = async () => {
    try {
      const response = await axios.get(getApiUrl("/api/user-associations"));
      if (response.status === 200 && Array.isArray(response.data)) {
        const mapped = response.data.map((assoc) => ({
          familyMemberName: assoc.name || "",
          associationKey: assoc.association_key || "",
          associationName: assoc.association_name || "",
        }));
        setFamilyAssociations(mapped);
      }
    } catch (err) {
      console.error("Error loading family associations:", err);
      setError("Unable to load associations. You can still view general results.");
    } finally {
      setIsLoadingSettings(false);
    }
  };

  // Called when user changes dropdown selection
  const handleFamilyMemberChange = (value) => {
    setSelectedAssociation(value);

    if (value === "All") {
      setAssociationNameForDisplay("");
      handleSubmitWithoutAssociation();
    } else {
      const assoc = familyAssociations.find((a) => a.associationKey === value);
      if (assoc) {
        setAssociationNameForDisplay(assoc.associationName);
        handleSubmitWithAssociation(assoc.associationKey);
      }
    }
  };

  // Handles the form submit button (works the same as changing selection)
  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");

    if (selectedAssociation === "All") {
      handleSubmitWithoutAssociation();
      return;
    }

    if (selectedAssociation) {
      // selectedAssociation is the associationKey
      const assoc = familyAssociations.find((a) => a.associationKey === selectedAssociation);
      if (assoc) {
        setAssociationNameForDisplay(assoc.associationName);
      }
      handleSubmitWithAssociation(selectedAssociation);
    } else {
      setError("Please select an association or choose All.");
    }
  };

  const handleSubmitWithAssociation = (associationKey) => {
    setIsLoading(true);
    setError("");
    const url = `https://entry.kcirodeo.net/RodeoResults.aspx?Producer=${encodeURIComponent(
      associationKey
    )}`;
    setIframeUrl(url);
    setIsLoading(false);
  };

  const handleSubmitWithoutAssociation = () => {
    setIsLoading(true);
    setError("");
    const url = "https://entry.kcirodeo.net/AssocMain.aspx?AssocId=PERF";
    setIframeUrl(url);
    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
          <GaugeIcon className="mr-2 h-6 w-6 sm:h-8 sm:w-8 text-black-500" />
          Live Results
        </h1>
        <p className="mt-2 text-sm sm:text-base text-gray-600">
          View live rodeo results by selecting an association or browse general results.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {familyAssociations.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {familyAssociations.length === 1 ? "Association" : "Select Association"}
            </label>

            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <select
                value={selectedAssociation}
                onChange={(e) => handleFamilyMemberChange(e.target.value)}
                className="w-full sm:w-80 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              >
                <option value="All">All - General Results</option>
                {familyAssociations.map((assoc) => (
                  <option key={assoc.associationKey} value={assoc.associationKey}>
                    {assoc.familyMemberName} - {assoc.associationName}
                  </option>
                ))}
              </select>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-[#51380d] text-white font-medium rounded-md hover:bg-[#3d2a0a] focus:outline-none focus:ring-2 focus:ring-[#51380d] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-sm sm:text-base"
              >
                {isLoading ? "Loading..." : "View Results"}
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-1">
              {familyAssociations.length === 1
                ? "Using your primary association for live results"
                : 'Choose which association to view live results for, or select "All" for general results'}
            </p>
          </div>
        )}
      </form>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3 sm:p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-2 sm:ml-3">
              <p className="text-xs sm:text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {iframeUrl && (
        <div className="space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              {selectedAssociation === "All"
                ? "General Live Results"
                : `Results for: ${associationNameForDisplay}`}
            </h2>
            <a
              href={iframeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs sm:text-sm text-blue-600 hover:text-blue-800 underline self-start sm:self-auto"
            >
              Open in new tab
            </a>
          </div>

          <div className="border border-gray-300 rounded-lg overflow-hidden relative">
            {isLoading && (
              <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-2 sm:mb-4"></div>
                  <p className="text-sm sm:text-base text-gray-600">Loading results...</p>
                </div>
              </div>
            )}
            <iframe
              src={iframeUrl}
              title={
                selectedAssociation === "All"
                  ? "General Live Results"
                  : `Live Results for ${associationNameForDisplay}`
              }
              className="w-full h-[500px] sm:h-[800px] border-0"
              sandbox="allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setError("Failed to load the results page. Please try again or open in a new tab.");
              }}
            />
          </div>
        </div>
      )}

      {!iframeUrl && (
        <div className="text-center py-8 sm:py-12 text-gray-500">
          {isLoadingSettings ? (
            <>
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-2 sm:mb-4"></div>
              <p className="text-base sm:text-lg">Loading your default association...</p>
            </>
          ) : (
            <>
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🏆</div>
              <p className="text-base sm:text-lg">
                {selectedAssociation === "All"
                  ? 'Select "All" above to view general live results'
                  : "Select an association above to view live results"}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
